<script setup>
// O CARTÃO DA SECRETÁRIA (21/10/2026). O Copiloto PROPÔS uma ação; nada foi feito. A pessoa vê a prévia exata do que
// será gravado e confirma (um clique) ou recusa. Feita, o cartão diz o que aconteceu, leva ao registro e oferece
// desfazer nos primeiros minutos. A decisão é do servidor (Secretaria::Decidir), que reconfere tudo.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import secretariaAPI from 'dashboard/api/secretaria';

const props = defineProps({
  acao: { type: Object, required: true },
});

const { t } = useI18n();
const P = 'CAPTAIN.COPILOT.SECRETARIA';
const estado = ref({ ...props.acao });
const ocupado = ref(null);
const erro = ref('');

const ICONES = {
  criar_tarefa: 'i-ph-calendar-plus-fill',
  concluir_atividade: 'i-ph-check-circle-fill',
  criar_nota: 'i-ph-note-pencil-fill',
};
const icone = computed(
  () => ICONES[estado.value.ferramenta] || 'i-ph-lightning-fill'
);
const status = computed(() => estado.value.status);
const linhas = computed(() => estado.value.previa?.linhas || []);
const titulo = computed(() => estado.value.previa?.titulo || '');

// A mensagem guarda o cartão como ele nasceu; reaberta depois, a proposta pode já ter sido decidida ou vencido.
onMounted(async () => {
  if (estado.value.status !== 'proposta' && !estado.value.desfazivel) return;
  try {
    const { data } = await secretariaAPI.show(estado.value.id);
    estado.value = { ...estado.value, ...data };
  } catch {
    // Sem conferir, fica o que veio na mensagem; o servidor reconfere no clique.
  }
});

const decidir = async operacao => {
  if (ocupado.value) return;
  ocupado.value = operacao;
  erro.value = '';
  try {
    const { data } = await secretariaAPI[operacao](estado.value.id);
    estado.value = { ...estado.value, ...data };
  } catch (falha) {
    const dados = falha?.response?.data;
    erro.value = dados?.error || t(`${P}.ERRO`);
    if (dados?.acao) estado.value = { ...estado.value, ...dados.acao };
  } finally {
    ocupado.value = null;
  }
};
</script>

<template>
  <section
    class="sec-acao"
    :class="`sec-acao--${status}`"
    :aria-label="titulo"
    data-testid="secretaria-acao"
  >
    <header class="sec-acao__cab">
      <span class="sec-acao__ico" aria-hidden="true"><i :class="icone" /></span>
      <span class="sec-acao__titulo">{{ titulo }}</span>
      <span v-if="status !== 'proposta'" class="sec-acao__selo">
        {{ t(`${P}.STATUS.${status}`) }}
      </span>
    </header>

    <ul class="sec-acao__linhas">
      <li v-for="(linha, i) in linhas" :key="i">{{ linha }}</li>
    </ul>

    <p v-if="erro" class="sec-acao__erro" role="alert">{{ erro }}</p>

    <div v-if="status === 'proposta'" class="sec-acao__botoes">
      <button
        type="button"
        class="sec-acao__btn sec-acao__btn--forte"
        :disabled="!!ocupado"
        data-testid="secretaria-confirmar"
        @click="decidir('confirmar')"
      >
        <i class="i-ph-check-bold" aria-hidden="true" />
        {{ ocupado === 'confirmar' ? t(`${P}.FAZENDO`) : t(`${P}.CONFIRMAR`) }}
      </button>
      <button
        type="button"
        class="sec-acao__btn"
        :disabled="!!ocupado"
        data-testid="secretaria-recusar"
        @click="decidir('recusar')"
      >
        {{ t(`${P}.RECUSAR`) }}
      </button>
      <span class="sec-acao__nota">{{ t(`${P}.NADA_FEITO`) }}</span>
    </div>

    <div v-else-if="status === 'feita'" class="sec-acao__botoes">
      <a
        v-if="estado.resultado?.link"
        class="sec-acao__btn sec-acao__btn--link"
        :href="estado.resultado.link"
      >
        {{ t(`${P}.ABRIR`) }}
        <i class="i-ph-arrow-right" aria-hidden="true" />
      </a>
      <button
        v-if="estado.desfazivel"
        type="button"
        class="sec-acao__btn"
        :disabled="!!ocupado"
        data-testid="secretaria-desfazer"
        @click="decidir('desfazer')"
      >
        <i class="i-ph-arrow-counter-clockwise" aria-hidden="true" />
        {{ t(`${P}.DESFAZER`) }}
      </button>
    </div>
  </section>
</template>

<style scoped>
/* A proposta pede a pessoa: âmbar (o mesmo tom do "Revisar e agendar"). Feita: verde. Recusada, vencida ou
   desfeita: apagada — fica no histórico, sem chamar atenção. */
.sec-acao {
  display: grid;
  gap: var(--esp-2, 8px);
  padding: 12px 14px;
  border-radius: 14px;
  background: rgb(var(--amber-9) / 8%);
  box-shadow: inset 0 0 0 1px rgb(var(--amber-9) / 35%);
}

.sec-acao--feita {
  background: rgb(var(--teal-9) / 8%);
  box-shadow: inset 0 0 0 1px rgb(var(--teal-9) / 35%);
}

.sec-acao--recusada,
.sec-acao--vencida,
.sec-acao--desfeita {
  background: rgb(var(--slate-3));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-6));
  opacity: 0.8;
}

.sec-acao__cab {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sec-acao__ico {
  display: grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: rgb(var(--amber-9));
  color: rgb(42 26 0);
}

.sec-acao--feita .sec-acao__ico {
  background: rgb(var(--teal-9));
  color: rgb(var(--white, 255 255 255));
}

.sec-acao--recusada .sec-acao__ico,
.sec-acao--vencida .sec-acao__ico,
.sec-acao--desfeita .sec-acao__ico {
  background: rgb(var(--slate-6));
  color: rgb(var(--slate-11));
}

.sec-acao__titulo {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: var(--txt-dado, 14px);
}

.sec-acao__selo {
  padding: 2px 8px;
  border-radius: 999px;
  background: rgb(var(--slate-12) / 8%);
  font-size: 12px;
  font-weight: 600;
}

.sec-acao__linhas {
  display: grid;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--txt-nota, 13px);
  color: rgb(var(--slate-12));
  overflow-wrap: anywhere;
}

.sec-acao__linhas li:first-child {
  font-weight: 600;
}

.sec-acao__erro {
  margin: 0;
  font-size: 13px;
  color: rgb(var(--ruby-11));
}

.sec-acao__botoes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.sec-acao__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 9px;
  background: rgb(var(--slate-12) / 6%);
  color: rgb(var(--slate-12));
  font-size: 13px;
  font-weight: 600;
  transition: background-color 0.15s ease;
}

.sec-acao__btn:hover:not(:disabled) {
  background: rgb(var(--slate-12) / 12%);
}

.sec-acao__btn:disabled {
  opacity: 0.6;
  cursor: progress;
}

.sec-acao__btn--forte {
  background: rgb(var(--blue-9));
  color: rgb(255 255 255);
}

.sec-acao__btn--forte:hover:not(:disabled) {
  background: rgb(var(--blue-10));
}

.sec-acao__btn--link {
  text-decoration: none;
}

.sec-acao__btn:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.sec-acao__nota {
  font-size: 12px;
  color: rgb(var(--slate-11));
}
</style>
